import { useEffect, useCallback } from "react";
import { isTvEnv } from "@/utils/common.helpers";

/**
 * Hook to capture numeric key presses (0-9) from TV remote
 * and inject them directly into input fields.
 *
 * This allows users to input numbers using the numeric buttons
 * on their TV remote without navigating the on-screen keyboard.
 *
 * @param value - Current input value
 * @param onChange - Callback to update the value
 * @param maxLength - Maximum allowed length (default: 10)
 * @param enabled - Whether to enable the listener (default: false)
 */
export const useTvNumericInput = (
  value: string,
  onChange: (value: string) => void,
  maxLength: number = 10,
  enabled: boolean = false,
): void => {
  const handleRemoteKeyPress = useCallback(
    (e: KeyboardEvent) => {
      // Capture numeric keys 0-9
      // stopPropagation prevents these from triggering other TV behaviors
      // (numeric keys are not used by spatial navigation)
      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        e.stopPropagation();
        if (value.length < maxLength) {
          onChange(value + e.key);
        }
      }
      // Handle Backspace for deletion - only when there's content to delete
      // This allows normal back navigation when input is empty
      if (e.key === "Backspace" && value.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        onChange(value.slice(0, -1));
      }
    },
    [value, onChange, maxLength],
  );

  useEffect(() => {
    // Only add listener on TV environment and when explicitly enabled
    if (!isTvEnv() || !enabled) return;

    window.addEventListener("keydown", handleRemoteKeyPress);
    return () => window.removeEventListener("keydown", handleRemoteKeyPress);
  }, [handleRemoteKeyPress, enabled]);
};
